import React from 'react';
import PropTypes from 'prop-types';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS
} from '../../../../component/common/designSystem';
import { SEQUENCE_RAMP, ACCURACY_RAMP } from './gamesTheme';
import {
  useGameDetail,
  orDash,
  asDuration,
  TrendPanel,
  WeekdayPanel,
  GameFootnote,
  BandBar
} from './GameShell';

/**
 * Games → Word Search.
 *
 * A play here is a LEVEL CLEARED, and the levels live nested inside a per-user
 * document rather than as rows of their own — which is why the backend dates
 * them by `dataStats.completedAt` and not by the document's own timestamp. A
 * person's document is created the first time they play and then appended to
 * for weeks.
 *
 * That structure creates one figure no other game has: somebody can hold a
 * document with no levels in it at all. They opened the game, a record was
 * written, and they never finished a board. "Opened, never finished a level" is
 * reported separately for exactly that reason — it is the difference between a
 * game people bounce off and one nobody has found.
 *
 * THE DIFFICULTY TABLE IS THE POINT OF THIS PAGE. If average score holds up as
 * difficulty climbs but hint use climbs with it, the harder boards are being
 * brute-forced rather than solved, and the difficulty curve needs work.
 *
 * HINT USE IS NOT FAILURE. The hint is part of the game. A rate near 100% means
 * the boards are unsolvable without it, which is a design signal, not a
 * judgement on the people playing.
 */
const WordSearchPage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, gate] = useGameDetail(fetcher);
  if (gate) return gate;

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Playing"
          value={formatBigNumber(stats?.players || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Levels Cleared"
          value={formatBigNumber(stats?.plays || 0)}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Levels Each"
          value={stats?.playsPerPlayer ?? 0}
        />
        <StatCard icon={STAT_ICONS.score} title="Average Score" value={orDash(stats?.avgScore)} />
        <StatCard
          icon={STAT_ICONS.time}
          title="Average Level"
          value={asDuration(stats?.avgSeconds)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Played On"
          value={formatBigNumber(stats?.activeDays || 0)}
        />
      </div>

      {/* The one figure this game's storage makes visible and no other does. */}
      {stats?.startedButUnplayed > 0 ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          {formatBigNumber(stats.startedButUnplayed)}{' '}
          {stats.startedButUnplayed === 1 ? 'person has' : 'people have'} opened Word Search without
          finishing a single level. They are not counted in the figures above — those cover people
          who cleared at least one board.
        </p>
      ) : null}

      {/* ---------- Shape of a level ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Which Difficulty"
          subtitle="Levels played, by the board's difficulty setting"
          data={stats?.difficultySplit || []}
          colors={SEQUENCE_RAMP}
          unit="levels"
          totalLabel="Levels"
          legendLimit={5}
        />
        <Panel title="How Long a Level Takes" subtitle="Time to clear one board">
          {(stats?.timeSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.timeSplit} colors={SEQUENCE_RAMP} unit="levels" />
          ) : (
            <PanelEmpty>No levels cleared yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Scores" subtitle="Points earned per level">
          {(stats?.scoreSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.scoreSplit} colors={ACCURACY_RAMP} unit="levels" />
          ) : (
            <PanelEmpty>No levels cleared yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- How far people get ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Far People Get"
          subtitle="Highest level each person has reached"
          data={stats?.reachSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <MiniDonutCard
          title="How Much People Play"
          subtitle="Levels cleared per person"
          data={stats?.volumeSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          <StatCard
            icon={STAT_ICONS.rating}
            title="Levels Using a Hint"
            value={`${stats?.hintRate ?? 0}%`}
          />
          <StatCard
            icon={STAT_ICONS.personalBest}
            title="Highest Level Reached"
            value={formatBigNumber(stats?.highestLevel || 0)}
          />
          <StatCard
            icon={STAT_ICONS.activeUsers}
            title={`Share of ${populationLabel}`}
            value={
              stats?.totalUsers > 0
                ? `${Math.round(((stats.players || 0) / stats.totalUsers) * 1000) / 10}%`
                : '-'
            }
          />
        </div>
      </div>

      {/* ---------- Difficulty against outcome ---------- */}
      <div className="mt-6">
        <Panel
          title="Does the Difficulty Curve Work?"
          subtitle="If the score holds up but hint use climbs, the harder boards are being brute-forced — a dash means too few levels to report"
        >
          {(stats?.difficultyTable || []).length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Difficulty</th>
                  <th className="py-2 px-4 font-medium text-center">Levels cleared</th>
                  <th className="py-2 px-4 font-medium text-center">Average score</th>
                  <th className="py-2 px-4 font-medium text-center">Used a hint</th>
                  <th className="py-2 font-medium text-right">Average time</th>
                </>
              }
            >
              {stats.difficultyTable.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.levels)}</td>
                  <td className="py-2 px-4 text-center">{orDash(row.avgScore)}</td>
                  <td className="py-2 px-4 text-center">
                    {row.hintRate == null ? '-' : `${row.hintRate}%`}
                  </td>
                  <td className="py-2 text-right">{asDuration(row.avgSeconds)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No difficulty has enough levels cleared to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Rhythm ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <TrendPanel stats={stats} subtitle="Levels cleared per day — last 30 days" />
        <WeekdayPanel stats={stats} />
      </div>

      <GameFootnote />
    </div>
  );
};

WordSearchPage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default WordSearchPage;
